import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import fr from 'facet-rest';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import MetaFormInline from 'Components/UI/Meta/MetaForm/Components/MetaFormInline';
import StepNavigator from 'Components/ComponentLibrary/Molecules/StepNavigator';
import useEventTrackingLogEvent, { categories, eventTypes } from 'EventTracking/useEventTrackingLogEvent';

const styles = theme => ({
  root: {
    margin: theme.spacing(2),
    marginBottom: theme.spacing(10),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  previousButton: {},
  continueButton: {
    margin: theme.spacing(0, 1),
  },
});

const WizardCollectionStepperTemplateContent = ({
  className,
  classes,
  template,
  layout,
  metaOverrideSet,
  resource,
  index,
  total,
  next,
  previous,
  isStart,
  isEnd,
}) => {
  const { logStandardizedEvent } = useEventTrackingLogEvent();
  const [errors, setErrors] = useState(null);
  const [loading, setLoading] = useState(false);
  const {
    pageContentResource,
    setPageContentCollection,
    loadPageContentLink,
    isPageContentLoading,
  } = usePageContentContext();
  const previousLink = pageContentResource.relationship(fr.relationships.previousLink.name)?.link('related');

  const onNext = async () => {
    setErrors(null);
    setLoading(true);
    if (isEnd) {
      logStandardizedEvent(categories.wizard, eventTypes.click, 'continue', { eventProperties: { templateName: template } });
      try {
        await resource.submit();
        const updatedColl = await pageContentResource.submit();
        await setPageContentCollection(updatedColl);
      } catch (e) {
        setErrors(e.errors);
        setLoading(false);
      }
    } else {
      try {
        await resource.submit();
        next();
      } catch (e) {
        setErrors(e.errors);
        setLoading(false);
      }
    }
    setLoading(false);
  };

  const onPrevious = async () => {
    logStandardizedEvent(categories.wizard, eventTypes.click, 'back', { eventProperties: { templateName: template } });
    setErrors(null);
    setLoading(true);
    if (isStart) {
      try {
        await loadPageContentLink(previousLink);
      } catch (e) {
        setErrors(e.errors);
        setLoading(false);
      }
    } else {
      previous();
    }
    setLoading(false);
  };

  return (
    <Box className={classNames(className, classes.root)}>
      <MetaFormInline
        noIndividualSubmitButtons
        resource={resource}
        layout={layout}
        metaOverrideSet={metaOverrideSet}
        LayoutProps={{
          index,
          total,
          onNext,
          onPrevious,
          isStart,
          isEnd,
          hideErrorList: true,
        }}
        errorsOverride={errors}
      />
      <StepNavigator
        disabled={isPageContentLoading || loading}
        isLoading={isPageContentLoading || loading}
        onNext={onNext}
        onPrevious={onPrevious}
      />
    </Box>
  );
};

WizardCollectionStepperTemplateContent.defaultProps = {
  className: '',
  metaOverrideSet: undefined,
};

WizardCollectionStepperTemplateContent.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  /** Template is used for Amplitude logging */
  template: PropTypes.string.isRequired,
  /** layout: The body content layout * */
  layout: PropTypes.object.isRequired,
  /** metaOverrideSet: A meta override set * */
  metaOverrideSet: PropTypes.object,
  /** resource: The displayed resource. Provied by withCollectionStepper HOC * */
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
  /** index: The index of the current resource in the collection. One based numbering (arrayIndex+1). Provied by withCollectionStepper HOC * */
  index: PropTypes.number.isRequired,
  /** total: The total resources in the collection. Provied by withCollectionStepper HOC * */
  total: PropTypes.number.isRequired,
  /** next: Function to load the next resource. Provied by withCollectionStepper HOC * */
  next: PropTypes.func.isRequired,
  /** previous: Function to load the previous resource. Provied by withCollectionStepper HOC * */
  previous: PropTypes.func.isRequired,
  /** isStart: True if the current resource is the first resource in the collection. Provied by withCollectionStepper HOC * */
  isStart: PropTypes.bool.isRequired,
  /** isEnd: True if the current resource is the last resource in the collection. Provied by withCollectionStepper HOC * */
  isEnd: PropTypes.bool.isRequired,
};

export default withStyles(styles)(WizardCollectionStepperTemplateContent);
